import {
  Box,
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { LayoutData, useLayout } from "../../hooks/pdv/useLayout";
import { Assento, AvisoPoltrona, Bilhete, Passagem } from "../../utils/interfaces";
import { useEffect, useMemo, useState } from "react";
import { ModalSelect } from "./ModalSelect";
import { Loading } from "../../components/Loading";
import { extrairAvisosReserva, exibirAvisosReserva, useMutationReserva } from "../../hooks/pdv/mutationReserva";
import { usePedidoContext } from "../../Context/PedidoContext";
import Swal from "sweetalert2";
import { transformDateLocaleToDateIso } from "../../utils/datetime";
import { format, isValid, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { normalizePassagem } from "../../utils/normalizePassagem";
import { api } from "../../libs/axios";
import { useLimites } from "../../hooks/pdv/useLimites";
import { formatBRL } from "../../utils/currency";
import { MENSAGEM_PENDENCIA_FINANCEIRA_NOVA_VENDA, refreshUser, temPendenciaFinanceira } from "../../libs/auth";

const LIMITE_BILHETES_IDA_PADRAO = 5;
const LIMITE_BILHETES_VOLTA_PADRAO = 5;
const LIMITE_BILHETES_TOTAL_PADRAO = 10;
const MENSAGEM_LIMITE_IDA_BACK = "Limite de bilhetes de ida excedido. Máximo de 5 bilhetes de ida por pedido.";
const MENSAGEM_LIMITE_VOLTA_BACK = "Limite de bilhetes de volta excedido. Máximo de 5 bilhetes de volta por pedido.";
const MENSAGEM_LIMITE_TOTAL_BACK = "Limite de bilhetes por pedido excedido. Máximo de 10 bilhetes por pedido.";
const MAX_ASSENTOS_POR_ANDAR = 48;

function normalizarContexto(contexto?: string): "ida" | "volta" {
  return String(contexto || "").trim().toLowerCase() === "volta" ? "volta" : "ida";
}

function extrairPedidoPayload(value: unknown): any {
  if (value && typeof value === "object" && "data" in (value as Record<string, unknown>)) {
    const maybeData = (value as { data?: unknown }).data;
    if (maybeData && typeof maybeData === "object") {
      return maybeData;
    }
  }

  return value;
}

function extrairAvisoDivergenciaPedido(value: unknown): AvisoPoltrona | undefined {
  const payload = extrairPedidoPayload(value);
  if (!payload || typeof payload !== "object" || !Array.isArray(payload.avisos)) {
    return undefined;
  }

  return payload.avisos.find((aviso: AvisoPoltrona) => deveExibirAvisoDivergenciaValor(aviso));
}

function extrairPrimeiroBilheteId(value: unknown): string | number | undefined {
  const payload = extrairPedidoPayload(value);
  if (!payload || typeof payload !== "object" || !Array.isArray(payload.bilhetes)) {
    return undefined;
  }

  return payload.bilhetes[0]?.id;
}

function encontrarBilheteEmPedido(pedidoResponse: any, aviso: any, assento: string, contexto: string): Bilhete | undefined {
  const bilhetes = Array.isArray(pedidoResponse?.bilhetes) ? pedidoResponse.bilhetes : [];
  const avisoBilheteId = aviso?.bilhete_id;

  if (avisoBilheteId) {
    return bilhetes.find((bilhete: Bilhete) => String(bilhete.id) === String(avisoBilheteId));
  }

  const assentoAviso = aviso?.assento ?? assento;
  const assentoNormalizado = String(Number(assentoAviso));
  const contextoNormalizado = normalizarContexto(contexto);

  return [...bilhetes].reverse().find((bilhete: Bilhete) => (
    String(Number(bilhete.assento)) === assentoNormalizado
      && normalizarContexto(bilhete.contexto) === contextoNormalizado
  ));
}

async function encontrarBilheteReservado(pedidoResponse: any, aviso: any, assento: string, contexto: string): Promise<Bilhete | undefined> {
  const bilheteLocal = encontrarBilheteEmPedido(pedidoResponse, aviso, assento, contexto);
  if (bilheteLocal) {
    return bilheteLocal;
  }

  const pedidoId = pedidoResponse?.id;
  if (!pedidoId) {
    return undefined;
  }

  const response = await api.get(`/passagens/${pedidoId}/carrinho`);
  return encontrarBilheteEmPedido(extrairPedidoPayload(response.data), aviso, assento, contexto);
}

const MENSAGEM_AVISO_DEVOLUCAO = 'Atenção: ao realizar o pagamento deste pedido, não será possível devolver, pois o embarque ocorre em menos de 3 horas.';
const CODIGO_AVISO_DIVERGENCIA_VALOR = 'divergencia_valor_poltrona';
const VALOR_MINIMO_AUMENTO_AVISO = 1;

function deveExibirAvisoDivergenciaValor(aviso?: AvisoPoltrona): boolean {
  if (!aviso || aviso.codigo !== CODIGO_AVISO_DIVERGENCIA_VALOR) {
    return false;
  }

  const valorOriginal = Number(aviso.valor_busca ?? aviso.valor_base_busca);
  const valorAtual = Number(aviso.valor_atual ?? aviso.valor_reserva ?? aviso.valor_base_reserva);

  return Number.isFinite(valorOriginal)
    && Number.isFinite(valorAtual)
    && Number((valorAtual - valorOriginal).toFixed(2)) >= VALOR_MINIMO_AUMENTO_AVISO;
}

interface EmbarqueSource {
  saidaHora?: string;
  saidaDia?: string;
  dataViagem?: string;
  embarque?: string;
}

function normalizarHoraParaIso(hora: string): string {
  const limpa = hora.trim();

  if (/^\d{2}:\d{2}$/.test(limpa)) {
    return `${limpa}:00`;
  }

  if (/^\d{2}:\d{2}:\d{2}$/.test(limpa)) {
    return limpa;
  }

  return limpa;
}

function parseEmbarque(passagem?: EmbarqueSource): Date | null {
  const saidaHora = (passagem?.saidaHora ?? '').trim();
  const saidaDia = (passagem?.saidaDia ?? '').trim();
  const dataViagem = (passagem?.dataViagem ?? '').trim();
  const embarqueRaw = (passagem?.embarque ?? '').trim();

  if (embarqueRaw !== '') {
    const candidatos = [embarqueRaw, embarqueRaw.replace(' ', 'T')];

    for (const candidato of candidatos) {
      const embarqueDate = new Date(candidato);
      if (isValid(embarqueDate)) {
        return embarqueDate;
      }
    }
  }

  const diaBase = saidaDia !== '' ? saidaDia : dataViagem;

  if (diaBase === '' || saidaHora === '') {
    return null;
  }

  const formatos = [
    'dd/MM/yyyy HH:mm',
    'dd/MM/yyyy HH:mm:ss',
    'yyyy-MM-dd HH:mm',
    'yyyy-MM-dd HH:mm:ss',
  ];

  for (const formatoData of formatos) {
    const parsed = parse(`${diaBase} ${saidaHora}`, formatoData, new Date(), { locale: ptBR });
    if (isValid(parsed)) {
      return parsed;
    }
  }

  const horaIso = normalizarHoraParaIso(saidaHora);
  const isoDia = diaBase.includes('/') ? diaBase.split('/').reverse().join('-') : diaBase;
  const fallback = new Date(`${isoDia}T${horaIso}`);
  if (isValid(fallback)) {
    return fallback;
  }

  return null;
}

function shouldWarnByDepartureWindow(...sources: Array<EmbarqueSource | undefined>): boolean {
  for (const source of sources) {
    const embarque = parseEmbarque(source);
    if (!embarque) {
      continue;
    }

    const now = new Date();
    const limite = new Date(embarque.getTime() - (3 * 60 * 60 * 1000));
    if (now >= limite && now <= embarque) {
      return true;
    }
  }

  return false;
}

function getStatusClass(item: Assento | null = null) {
  if (item === null)
      return { bg: 'transparent', color: 'white'};

  switch (item.ocupado) {
      case 1: return  { bg: 'red.500', color: 'white'};
      case 0: return  { bg: 'green.500', color: 'white'};
      default: return  { bg: 'transparent', color: 'white'};
  }
}

export function criarMatriz(assentos: Assento[]): (Assento)[][] {
  const matriz: (Assento)[][] = [];
  for (const item of assentos) {
    const x = parseInt(item.x, 10); // Converte x para number
    const y = parseInt(item.y, 10); // Converte y para number

    matriz[x] = matriz[x] || [];
    matriz[x][y] = item; // Assento inserido na posição correta
  }
  return matriz;
}

function normalizarAndarAssento(assento: Assento): number {
  const andar = Number(assento.z);

  return Number.isFinite(andar) && andar > 0 ? andar : 1;
}

function getNumeroAssento(assento: Assento): number | null {
  const assentoComAliases = assento as Assento & { number?: string | number; numero?: string | number };
  const numero = Number(assentoComAliases.assento ?? assentoComAliases.number ?? assentoComAliases.numero);

  return Number.isFinite(numero) && numero > 0 ? numero : null;
}

function getQuantidadeAndaresCalculada(assentos: Assento[]): number {
  let maiorNumeroAssento = 0;
  let quantidadeAssentosNumericos = 0;
  let maiorAndarOriginal = 1;

  for (const assento of assentos) {
    maiorAndarOriginal = Math.max(maiorAndarOriginal, normalizarAndarAssento(assento));

    const numero = getNumeroAssento(assento);
    if (numero === null) {
      continue;
    }

    quantidadeAssentosNumericos += 1;
    maiorNumeroAssento = Math.max(maiorNumeroAssento, numero);
  }

  return Math.max(
    maiorAndarOriginal,
    Math.ceil(maiorNumeroAssento / MAX_ASSENTOS_POR_ANDAR),
    Math.ceil(quantidadeAssentosNumericos / MAX_ASSENTOS_POR_ANDAR),
    1
  );
}

function getMaiorNumeroAssento(assentos: Assento[]): number {
  return assentos.reduce((maiorNumero, assento) => {
    const numero = getNumeroAssento(assento);

    return numero === null ? maiorNumero : Math.max(maiorNumero, numero);
  }, 0);
}

function deveSepararAndaresPorQuantidade(assentos: Assento[]): boolean {
  const andaresOriginais = new Set(assentos.map(normalizarAndarAssento));

  return andaresOriginais.size === 1 && getQuantidadeAndaresCalculada(assentos) > 1;
}

function calcularAndarVisual(
  assento: Assento,
  separarPorQuantidade: boolean,
  index: number,
  separarPorOrdem: boolean,
  quantidadeAndares: number
): number {
  if (!separarPorQuantidade) {
    return normalizarAndarAssento(assento);
  }

  if (separarPorOrdem) {
    return quantidadeAndares - Math.floor(index / MAX_ASSENTOS_POR_ANDAR);
  }

  const numero = getNumeroAssento(assento);
  if (numero !== null) {
    return quantidadeAndares - Math.ceil(numero / MAX_ASSENTOS_POR_ANDAR) + 1;
  }

  const x = Number(assento.x);
  if (!Number.isFinite(x)) {
    return 1;
  }

  return x > 13 ? 2 : 1;
}

function getAndaresDisponiveis(assentos: Assento[]): number[] {
  const separarPorQuantidade = deveSepararAndaresPorQuantidade(assentos);
  const quantidadeAndares = getQuantidadeAndaresCalculada(assentos);

  if (separarPorQuantidade) {
    return Array.from({ length: quantidadeAndares }, (_, index) => index + 1);
  }

  return Array.from(new Set(assentos.map(normalizarAndarAssento)))
    .sort((a, b) => a - b);
}

export function criarMatrizesPorAndar(assentos: Assento[]): Record<number, Assento[][]> {
  const separarPorQuantidade = deveSepararAndaresPorQuantidade(assentos);
  const separarPorOrdem = separarPorQuantidade && getMaiorNumeroAssento(assentos) <= MAX_ASSENTOS_POR_ANDAR;
  const quantidadeAndares = getQuantidadeAndaresCalculada(assentos);

  return assentos.reduce<Record<number, Assento[][]>>((matrizes, assento, index) => {
    const andar = calcularAndarVisual(assento, separarPorQuantidade, index, separarPorOrdem, quantidadeAndares);

    matrizes[andar] = matrizes[andar] || [];
    const x = parseInt(assento.x, 10);
    const y = parseInt(assento.y, 10);

    matrizes[andar][x] = matrizes[andar][x] || [];
    matrizes[andar][x][y] = assento;

    return matrizes;
  }, {});
}


export function Layout() {
  const {recuperar, save, pedido,setListaLayoutSelect, listSelects, removerAssentoLayout } = usePedidoContext()
  const { data: limitesData } = useLimites();
  const limiteBilhetesIda = Number(limitesData?.limiteBilhetesIda ?? LIMITE_BILHETES_IDA_PADRAO);
  const limiteBilhetesVolta = Number(limitesData?.limiteBilhetesVolta ?? LIMITE_BILHETES_VOLTA_PADRAO);
  const limiteBilhetesTotal = Number(limitesData?.limiteBilhetesTotal ?? LIMITE_BILHETES_TOTAL_PADRAO);

  const mutation = useMutationReserva({ pedido })

  const {isOpen, onClose, onOpen} = useDisclosure()
  const {
    isOpen: isModalDivergenciaOpen,
    onOpen: onOpenModalDivergencia,
    onClose: onCloseModalDivergencia,
  } = useDisclosure()

  
  const [select, setSelect] = useState<Assento | undefined>(undefined)
  const [avisoDivergencia, setAvisoDivergencia] = useState<AvisoPoltrona | undefined>(undefined)
  const [avisoDivergenciaConfirmado, setAvisoDivergenciaConfirmado] = useState(false)
  const [reservaPendente, setReservaPendente] = useState<unknown>(undefined)
  const [andarSelecionado, setAndarSelecionado] = useState(1)

  const {watch, getValues} = useFormContext()
  const layout = watch('layout')
  const viagemSelecionada = typeof layout !== "undefined" && layout !== null;

  async function validarBloqueioFinanceiroNovaVenda() {
    await refreshUser().catch(() => undefined);

    if (!temPendenciaFinanceira()) {
      return false;
    }

    await Swal.fire({
      toast: false,
      position: "center",
      showConfirmButton: true,
      icon: "warning",
      title: "Pendência financeira",
      text: MENSAGEM_PENDENCIA_FINANCEIRA_NOVA_VENDA,
      confirmButtonText: "Ciente",
      allowEscapeKey: false,
      allowOutsideClick: false,
    });

    return true;
  }

  const {data, isLoading} = useLayout({passagem: layout});
  const layoutData = data;
  const temLayout = Array.isArray(layoutData?.assentos) && layoutData.assentos.length > 0;
  const assentosLayout = useMemo(
    () => (Array.isArray(layoutData?.assentos) ? layoutData.assentos : []),
    [layoutData?.assentos]
  );
  const andaresDisponiveis = useMemo(
    () => getAndaresDisponiveis(assentosLayout),
    [assentosLayout]
  );
  const matrizesPorAndar = useMemo(
    () => criarMatrizesPorAndar(assentosLayout),
    [assentosLayout]
  );
  const andarAtivo = andaresDisponiveis.includes(andarSelecionado)
    ? andarSelecionado
    : (andaresDisponiveis[0] ?? 1);
  const matriz: Assento[][] = temLayout ? (matrizesPorAndar[andarAtivo] ?? []) : [];

  useEffect(() => {
    if (!temLayout) {
      setAndarSelecionado(1);
      return;
    }

    if (!andaresDisponiveis.includes(andarSelecionado)) {
      setAndarSelecionado(andaresDisponiveis[0] ?? 1);
    }
  }, [andaresDisponiveis, andarSelecionado, temLayout]);
  const bilhetesParaContador: Bilhete[] = (() => {
    const bilhetesPedido = Array.isArray(pedido?.bilhetes) ? pedido.bilhetes : [];
    if (bilhetesPedido.length > 0) {
      return bilhetesPedido;
    }

    try {
      const carrinhoRaw = localStorage.getItem("carrinho");
      if (!carrinhoRaw) {
        return bilhetesPedido;
      }

      const carrinhoParsed = extrairPedidoPayload(JSON.parse(carrinhoRaw));
      return Array.isArray(carrinhoParsed?.bilhetes) ? carrinhoParsed.bilhetes : bilhetesPedido;
    } catch (_) {
      return bilhetesPedido;
    }
  })();

  const contadorBilhetes = bilhetesParaContador.reduce(
    (acc: { ida: number; volta: number }, bilhete: Bilhete) => {
      if (normalizarContexto(bilhete.contexto) === "volta") {
        acc.volta += 1;
      } else {
        acc.ida += 1;
      }

      return acc;
    },
    { ida: 0, volta: 0 }
  );
  const totalBilhetesSelecionados = contadorBilhetes.ida + contadorBilhetes.volta;

  const getPedidoIdDisponivel = (): string | undefined => {
    if (pedido?.id) {
      return String(pedido.id);
    }

    try {
      const carrinhoRaw = localStorage.getItem("carrinho");
      if (!carrinhoRaw) {
        return undefined;
      }

      const carrinhoParsed = extrairPedidoPayload(JSON.parse(carrinhoRaw));
      if (carrinhoParsed?.id) {
        return String(carrinhoParsed.id);
      }
    } catch (_) {
      return undefined;
    }

    return undefined;
  };

  const valorNovoAvisoDivergencia = Number(
    avisoDivergencia?.valor_reserva
    ?? avisoDivergencia?.valor_base_reserva
    ?? layoutData?.preco
    ?? layoutData?.precoBase
    ?? 0
  );

  const carregarBilhetesAtuais = async (): Promise<Bilhete[]> => {
    const bilhetesLocais: Bilhete[] = Array.isArray(pedido?.bilhetes) ? pedido.bilhetes : [];
    const pedidoId = getPedidoIdDisponivel();

    if (!pedidoId) {
      return bilhetesLocais;
    }

    try {
      const response = await api.get(`/passagens/${pedidoId}/carrinho`);
      const pedidoServidor = extrairPedidoPayload(response.data);
      const bilhetesServidor: Bilhete[] = Array.isArray(pedidoServidor?.bilhetes)
        ? pedidoServidor.bilhetes
        : [];

      return bilhetesServidor;
    } catch (_) {
      return bilhetesLocais;
    }
  };

  const getAvisoDivergenciaValor = (assento: Assento): AvisoPoltrona | undefined => {
    const avisoBackend = assento.avisos?.find(deveExibirAvisoDivergenciaValor)
      ?? layoutData?.avisos?.find(deveExibirAvisoDivergenciaValor);

    if (avisoBackend) {
      return avisoBackend;
    }

    const precoBaseBuscaRaw = (layout as Passagem | undefined)?.precoBase;
    const precoBaseAtualRaw = layoutData?.precoBase;
    const valorBaseBusca = Number(precoBaseBuscaRaw);
    const valorBaseAtual = Number(precoBaseAtualRaw);
    if (
      precoBaseBuscaRaw !== null
      && precoBaseBuscaRaw !== undefined
      && precoBaseAtualRaw !== null
      && precoBaseAtualRaw !== undefined
      &&
      Number.isFinite(valorBaseBusca)
      && Number.isFinite(valorBaseAtual)
      && Number((valorBaseAtual - valorBaseBusca).toFixed(2)) >= VALOR_MINIMO_AUMENTO_AVISO
    ) {
      return {
        tipo: "warning",
        codigo: CODIGO_AVISO_DIVERGENCIA_VALOR,
        valor_base_busca: valorBaseBusca,
        valor_base_reserva: valorBaseAtual,
      };
    }
  };

  const abrirFormularioPoltrona = async (assento: Assento) => {
    if (await validarBloqueioFinanceiroNovaVenda()) {
      return;
    }

    setListaLayoutSelect(assento)
    setSelect(assento)
    setAvisoDivergencia(undefined)
    setAvisoDivergenciaConfirmado(false)
    onOpen()
  };

  const confirmarAvisoDivergencia = () => {
    setAvisoDivergenciaConfirmado(true)
    onCloseModalDivergencia()
    const response = reservaPendente;
    setReservaPendente(undefined)
    if (response) {
      finalizarReserva(response)
    } else {
      void reservarPassagemSelecionada({
        ignorarAvisoDivergencia: true,
      })
    }
  };

  const cancelarAvisoDivergencia = async () => {
    const pedidoResponse = extrairPedidoPayload(reservaPendente);
    const selecionada = getValues("selecionada");
    const assento = avisoDivergencia?.assento ?? select?.assento ?? "";
    const contexto = selecionada?.contexto ?? "ida";
    const bilhete = reservaPendente && assento
      ? await encontrarBilheteReservado(pedidoResponse, avisoDivergencia, assento, contexto)
      : undefined;
    const bilheteId = avisoDivergencia?.bilhete_id ?? bilhete?.id ?? extrairPrimeiroBilheteId(reservaPendente);

    if (bilheteId) {
      try {
        await api.delete(`/pedido/cancelar/bilhete/${bilheteId}`, {
          params: {
            descartar_reserva_local: 1,
          },
        });
        recuperar();
      } catch (_) {
        await Swal.fire({
          title: "Erro ao excluir poltrona",
          text: "A reserva foi criada, mas não foi possível excluir a poltrona automaticamente. Tente pela lixeira da lista de poltronas selecionadas.",
          icon: "error",
        });
      }
    }

    removerAssentoLayout(select?.assento)
    removerAssentoLayout(bilhete?.assento)
    setSelect(undefined)
    setAvisoDivergencia(undefined)
    setAvisoDivergenciaConfirmado(false)
    setReservaPendente(undefined)
    onCloseModalDivergencia()
  };

  function Linha(item: Assento[]){
    const cells = [];
  
    for (let i = 0; i < 5; i++) {
      const assentoAtual = item[i];
      const styles = getStatusClass(assentoAtual ?? null)
      const inList = Boolean(assentoAtual) && listSelects.some(
        (s) => s.assento === assentoAtual?.assento && s.servico === assentoAtual?.servico
      )
      // Verifica se há um assento no índice atual, caso contrário usa um marcador vazio
      cells.push(
        <Box cursor={assentoAtual?.assento ? 'pointer' : 'default'} 
        onClick={async () => {
          if (!assentoAtual?.assento) {
            return;
          }

          if(inList || assentoAtual.ocupado === 1){
            Swal.fire({
              position: "center",
              title: "Atenção!",
              text: "Assento já está ocupado.",
              icon: 'warning',
              confirmButtonText: "Ciente",
              toast: false,
              allowEscapeKey: false,
              allowOutsideClick: false,
            })
          }else{
            const selecionada = getValues("selecionada");
            const contextoSelecionado = normalizarContexto(selecionada?.contexto);
            const bilhetes: Bilhete[] = await carregarBilhetesAtuais();

            const contagem = bilhetes.reduce(
              (acc: { ida: number; volta: number }, bilhete: Bilhete) => {
                if (normalizarContexto(bilhete.contexto) === "volta") {
                  acc.volta += 1;
                } else {
                  acc.ida += 1;
                }

                return acc;
              },
              { ida: 0, volta: 0 }
            );

            const idaTotal = contagem.ida + (contextoSelecionado === "ida" ? 1 : 0);
            const voltaTotal = contagem.volta + (contextoSelecionado === "volta" ? 1 : 0);
            const totalBilhetes = idaTotal + voltaTotal;

            let mensagemLimite: string | null = null;

            if (idaTotal > limiteBilhetesIda) {
              mensagemLimite = MENSAGEM_LIMITE_IDA_BACK;
            } else if (voltaTotal > limiteBilhetesVolta) {
              mensagemLimite = MENSAGEM_LIMITE_VOLTA_BACK;
            } else if (totalBilhetes > limiteBilhetesTotal) {
              mensagemLimite = MENSAGEM_LIMITE_TOTAL_BACK;
            }

            if (mensagemLimite) {
              const mensagemLimiteFormatada = mensagemLimite.replace(". Máximo", ".<br/>Máximo");

              Swal.fire({
                position: "center",
                title: "Atenção!",
                html: mensagemLimiteFormatada,
                icon: "warning",
                confirmButtonText: "Ciente",
                toast: false,
                allowEscapeKey: false,
                allowOutsideClick: false,
              });
              return;
            }

            const mensagemAviso = typeof layoutData?.mensagemServico === 'string' && layoutData.mensagemServico.trim() !== ''
              ? layoutData.mensagemServico
              : (
                shouldWarnByDepartureWindow(
                  layoutData as LayoutData & EmbarqueSource,
                  layout as Passagem | undefined
                )
                  ? MENSAGEM_AVISO_DEVOLUCAO
                  : ''
              );

            if (mensagemAviso !== '') {
              await Swal.fire({
                toast: false,
                position: 'center',
                showConfirmButton: true,
                icon: 'warning',
                title: 'Atenção',
                text: mensagemAviso,
              })
            }
            await abrirFormularioPoltrona(assentoAtual)
          }
        }} 
        fontSize={'small'} 
        key={i} 
        rounded={'md'} 
        textAlign="center" 
        bg={inList ? 'yellow.500' : styles.bg} 
        color={styles.color}>
          <Text mx={'4px'}>{assentoAtual?.assento || ""}</Text>
          <Box mb={'1px'} border={'2px solid #fff'} p={1} borderRadius={'lg'}></Box>
        </Box>
      );
    }
  
    return cells;
  }

  function handleClose(){
    onClose()
    removerAssentoLayout(select?.assento)
    setSelect(undefined)
    setAvisoDivergencia(undefined)
    setAvisoDivergenciaConfirmado(false)
    setReservaPendente(undefined)
  }

  async function onSubmitPassagem(){
    if (await validarBloqueioFinanceiroNovaVenda()) {
      return;
    }

    const aviso = select ? getAvisoDivergenciaValor(select) : undefined;
    if (aviso && !avisoDivergenciaConfirmado) {
      setAvisoDivergencia(aviso)
      onClose()
      onOpenModalDivergencia()
      return;
    }

    await reservarPassagemSelecionada()
  }

  async function reservarPassagemSelecionada({
    ignorarAvisoDivergencia = false,
  }: {
    ignorarAvisoDivergencia?: boolean;
  } = {}){
    try {
      if (await validarBloqueioFinanceiroNovaVenda()) {
        return;
      }

      const values = getValues()
      const selecionada = normalizePassagem(values.selecionada);
      const assento = String(select!.assento).padStart(2, "0");
  
      const response = await mutation.mutateAsync({
        adaptador: selecionada.adaptador,
        assento,
        cidadeOrigemId: selecionada.cidadeOrigemId,
        cidadeDestinoId: selecionada.cidadeDestinoId,
        data: format(values.data_saida, "yyyy-MM-dd", { locale: ptBR}),
        desembarque: selecionada.desembarque,
        empresa: selecionada.empresa,
        grupo: selecionada.grupo,
        classe: selecionada.classe,
        destino: selecionada.destino,
        identificador: selecionada.identificador,
        origem: selecionada.origem,
        preco: String(selecionada.preco),
        precoBase: selecionada.precoBase,
        precoSemViagemPremiada: selecionada.precoSemViagemPremiada,
        saida_hora: selecionada.saidaHora,
        servico: selecionada.servico,
        viagem_premiada: values.passageiro.viagem_premiada,
        viacao_id: selecionada.viacao?.id,
        nome: values.passageiro.nome,
        documento: values.passageiro.documento,
        data_nascimento: transformDateLocaleToDateIso(values.passageiro.data_nascimento),
        telefone: values.passageiro.telefone,
        contexto: selecionada.contexto,
        provedorWebviagem: selecionada.provedorWebviagem
      })

      const avisoReserva = extrairAvisoDivergenciaPedido(response);
      if (avisoReserva && !ignorarAvisoDivergencia) {
        setReservaPendente(response)
        setAvisoDivergencia(avisoReserva)
        save(response as any)
        recuperar()
        onClose()
        onOpenModalDivergencia()
        return;
      }

      const pedidoResponse = extrairPedidoPayload(response);
      const avisosReserva = extrairAvisosReserva(response);

      if (avisosReserva.length > 0) {
        save(pedidoResponse)
        recuperar()
        onClose()
      }

      const decisaoAviso = await exibirAvisosReserva(avisosReserva);

      if (decisaoAviso === "excluir") {
        const aviso = avisosReserva[0];
        const bilhete = await encontrarBilheteReservado(pedidoResponse, aviso, assento, selecionada.contexto);

        if (bilhete?.id) {
          await api.delete(`/pedido/cancelar/bilhete/${bilhete.id}?descartar_reserva_local=1`);
          removerAssentoLayout(select?.assento);
          removerAssentoLayout(bilhete.assento);
          setSelect(undefined);
          recuperar();
        } else {
          Swal.fire({
            title: "Atenção",
            icon: "warning",
            text: "Não foi possível localizar a poltrona reservada para excluir.",
          });
        }

        return;
      }

      if (!decisaoAviso && !avisoReserva) {
        Swal.fire({
          title: "Poltrona reservada",
        });
      }

      finalizarReserva(pedidoResponse)
    }catch(e){
      console.log(e)
    }
  }

  function finalizarReserva(response: unknown) {
    save(response as any)
    recuperar()
    onClose()
    setSelect(undefined)
    setAvisoDivergencia(undefined)
    setAvisoDivergenciaConfirmado(false)
    setReservaPendente(undefined)
  }

  return (
    <Box maxWidth={"200px"} width={"100%"} justifyContent={'start'} display={'flex'} flexDir={'column'}>
      <Text fontWeight={'bold'} textAlign={'center'} mb={2}>Seleção de Poltronas</Text>
      {viagemSelecionada && (
        <Box
          mb={2}
          px={2}
          py={2}
          borderRadius={"md"}
          bg={"#fffaf0"}
          border={"1px solid #f6ad55"}
        >
          <Text fontSize={"sm"} textAlign={"center"} color={"#7b341e"} fontWeight={"bold"}>
            Ida {contadorBilhetes.ida}/{limiteBilhetesIda} | Volta {contadorBilhetes.volta}/{limiteBilhetesVolta}
          </Text>
          <Text fontSize={"sm"} textAlign={"center"} color={"#7b341e"}>
            Total {totalBilhetesSelecionados}/{limiteBilhetesTotal}
          </Text>
        </Box>
      )}
      {temLayout && andaresDisponiveis.length > 1 && (
        <Flex mb={2} gap={1} justifyContent={"center"} flexWrap={"wrap"}>
          {andaresDisponiveis.map((andar) => (
            <Button
              key={`andar-${andar}`}
              size={"xs"}
              minW={"78px"}
              colorScheme={andarAtivo === andar ? "orange" : "gray"}
              variant={andarAtivo === andar ? "solid" : "outline"}
              onClick={() => setAndarSelecionado(andar)}
            >
              {andar}º andar
            </Button>
          ))}
        </Flex>
      )}
      <Flex px={12} py={0} gap={4}>
        <Box
          width={'20px'}
          height={'4px'}
          borderRadius={"6px"}
          backgroundColor={"#ffe7a0"}
          margin={"0 auto"}
          border={"1px solid #ccc"}
        ></Box>
        <Box
          width={'20px'}
          height={'4px'}
          borderRadius={"6px"}
          backgroundColor={"#ffe7a0"}
          margin={"0 auto"}
          border={"1px solid #ccc"}
        ></Box>
      </Flex>
      <Flex
        display={"block"}
        padding={"40px 4px"}
        border={"1px solid #333"}
        borderRadius={"50px 50px 24px 24px"}
        boxShadow={'0 0 3px #ccc'}
        minHeight={'440px'}
      >
          {isLoading ? (
            <Box py={6}>
              <Text textAlign={"center"} color={"gray.600"} fontSize={"sm"}>
                Carregando poltronas...
              </Text>
            </Box>
          ) : temLayout ? (
            matriz.map((item, index) => (
              <SimpleGrid key={`matriz-key-${index}`} my={1} columns={5} gap={2}>
                {Linha(item)}
              </SimpleGrid>
            ))
          ) : (
            <Box py={6}>
              <Text textAlign={"center"} color={"gray.600"} fontSize={"sm"}>
                Selecione uma viagem para exibir as poltronas.
              </Text>
            </Box>
          )}
      </Flex>
      <Divider my={2} />
      <Box fontSize={'x-small'}>
        <Flex justifyContent={'start'}>
          <Box rounded={'md'} textAlign="center" bg={'green.500'} color={'white'}>
            <Text mx={'4px'}>{"00"}</Text>
            <Box mb={'1px'} border={'2px solid #fff'} p={'1px'} borderRadius={'lg'}></Box>
          </Box>
          <Text>Livre</Text>
        </Flex>
        <Flex>
        <Box rounded={'md'} textAlign="center" bg={'red.500'} color={'white'}>
          <Text mx={'4px'}>{"00"}</Text>
          <Box mb={'1px'} border={'2px solid #fff'} p={'1px'} borderRadius={'lg'}></Box>
        </Box>
        <Text>Ocupada</Text>
      </Flex>
      <Flex>
        <Box rounded={'md'} textAlign="center" bg={'yellow.500'} color={'white'}>
          <Text mx={'4px'}>{"00"}</Text>
          <Box mb={'1px'} border={'2px solid #fff'} p={'1px'} borderRadius={'lg'}></Box>
        </Box>
        <Text>Selecionada</Text>
      </Flex>
      </Box>
      <Portal>
        {select && (
          <ModalSelect handleSubmitPassageiro={onSubmitPassagem} assento={select} isOpen={isOpen} onClose={() => handleClose()}/>
        )}
        <Modal
          isOpen={isModalDivergenciaOpen}
          onClose={() => void cancelarAvisoDivergencia()}
          isCentered
          closeOnOverlayClick={false}
          closeOnEsc={false}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontSize={"xl"}>⚠️ Atenção!</ModalHeader>
            <ModalBody display={"flex"} flexDir={"column"} gap={4}>
              <Text>O valor da poltrona selecionada foi alterado pela empresa de ônibus.</Text>
              <Text fontWeight={"bold"}>
                Novo valor: {formatBRL(valorNovoAvisoDivergencia)}
              </Text>
              <Text>
                Se não concordar com o novo valor, exclua esta poltrona e selecione outra antes de continuar.
              </Text>
            </ModalBody>
            <ModalFooter gap={3}>
              <Button variant={"outline"} onClick={() => void cancelarAvisoDivergencia()}>
                Excluir poltrona
              </Button>
              <Button colorScheme={"orange"} onClick={confirmarAvisoDivergencia}>
                Continuar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {mutation.isPending && <Loading />}
      </Portal>
    </Box>
  );
}
